import type { ZoneSource } from '@features/fields/api/useZoneTuning'
import { AutoBoundaryStatus } from '@features/fields/hooks/useBoundaryAutogen'
import {
  FilePlanAssetModalType,
  FilePlanAssetUploader
} from '@features/operations/routes/Planning/Fields'
import { getAllBoundariesApi, getBoundaryApi } from 'api-validations/locutus'
import { GuidancePrimitive } from 'api-validations/locutus/guidance'
import { ControllableApplication } from 'api-validations/stitch'
import { useEffect, useState } from 'react'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { usePanoramaMutation, usePanoramaQuery } from 'shared-ui/api-provider'
import { ButtonKind, PanoramaButton, PanoramaSelectMenu } from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { useLocalization } from 'shared-ui/localization'
import { resetZoneEditing, useDefaultLegendConfig, useMapContext } from 'shared-ui/map'
import { isNil, isNonNil } from 'utils'

import {
  AutoGenerateSelection,
  BottomButtons,
  Container,
  type CreationChoices,
  Mode
} from './BoundaryOptions'
import { PRESCRIPTION_BASE_ZONE_ID, withZone } from './ReusedComponents'
import { ZoneSourcePicker } from './ZoneSourcePicker'

export const AutoGenerateOptions = ({
  setBoundaryId,
  autoBoundaryStatus,
  fieldId,
  onAutoSelection,
  mode,
  onCancel,
  creationChoices,
  setCreationChoices,
  processSavedAssets,
  picksOwnLayer,
  zoneSource,
  onZoneSourceChange
}: {
  setBoundaryId: (id: string) => void
  autoBoundaryStatus: AutoBoundaryStatus
  fieldId: string
  onAutoSelection: () => void
  mode: Mode.Boundary | Mode.Prescription | Mode.Guidance
  onCancel: () => void
  creationChoices: CreationChoices
  setCreationChoices: React.Dispatch<React.SetStateAction<CreationChoices>>
  processSavedAssets: (
    assets: {
      id: string
      name: string
      type?: ControllableApplication | GuidancePrimitive
    }[]
  ) => void
  picksOwnLayer: boolean
  zoneSource: ZoneSource | undefined
  onZoneSourceChange: (source: ZoneSource | undefined) => void
}) => {
  const { map } = useMapContext()
  const { t } = useLocalization()
  const orgCode = useOrgCodeOrThrow()

  const boundaries = usePanoramaQuery(
    getAllBoundariesApi,
    { orgCode },
    {
      select: ({ items }) => items.filter((item) => item.fieldId === fieldId)
    }
  )

  const loadBoundaryPreview = usePanoramaMutation(getBoundaryApi, {
    onSuccess: ({ data }) => {
      map.drawTool.clear()
      map.drawTool.addFeatures(withZone(data.boundary, PRESCRIPTION_BASE_ZONE_ID))
    }
  })

  const guidanceCreationOptions = [
    {
      title: t(($) => $.planning.guidancePlanTitle),
      type: 'autoCreate',
      button: t(($) => $.planning.boundaryCreation.autoCreate.button),
      icon: SpriteMap.GuidanceIcon,
      auto: AutoGenerateSelection.Auto
    }
  ] as const

  const creationOptions = [
    {
      type: 'import',
      button: t(($) => $.buttons.import),
      icon: SpriteMap.UploadIcon,
      auto: AutoGenerateSelection.Unselected
    },
    {
      type: 'autoCreate',
      button: t(($) => $.planning.boundaryCreation.autoCreate.button),
      icon: SpriteMap.GenerateIcon,
      auto: AutoGenerateSelection.Auto
    },
    {
      type: 'draw',
      button: t(($) => $.planning.boundaryCreation.draw.title),
      icon: SpriteMap.EditIcon,
      auto: AutoGenerateSelection.Manual
    }
  ] as const

  const defaultConfig = useDefaultLegendConfig().data

  const effectiveBoundaryId =
    mode !== Mode.Boundary && isNonNil(creationChoices.boundaryId)
      ? creationChoices.boundaryId
      : null

  const isLoadingAutoCreateSource =
    mode === Mode.Boundary && autoBoundaryStatus === AutoBoundaryStatus.Loading
  const lacksOwnLayer = picksOwnLayer && isNil(zoneSource)
  const noAutoCreateSource =
    mode === Mode.Boundary ? autoBoundaryStatus === AutoBoundaryStatus.Unavailable : lacksOwnLayer
  const noAutoCreateSourceReason =
    mode === Mode.Boundary
      ? t(($) => $.planning.boundaryCreation.noPasses)
      : t(($) => $.planning.boundaryCreation.warningNoLayer)
  const autoCreateDisabled =
    (mode === Mode.Guidance && isNil(effectiveBoundaryId)) ||
    (mode !== Mode.Guidance && noAutoCreateSource) ||
    (mode === Mode.Prescription && !picksOwnLayer && defaultConfig?.isDiscrete !== false)
  const [upload, setUpload] = useState<FilePlanAssetModalType>()

  // On mount: if there's no boundary selected, clear the map for a clean starting state.
  // Cleanup: reset zone editing state (active zone, color index) whenever this screen is exited.
  useEffect(() => {
    if (isNil(effectiveBoundaryId)) map.drawTool.clear()
    else
      loadBoundaryPreview.mutate({
        params: { orgCode, boundaryId: effectiveBoundaryId }
      })
    return resetZoneEditing
  }, [])

  return (
    <div className="flex flex-col gap-4">
      <Container mode={creationChoices.type}>
        {creationChoices.type !== Mode.Boundary && (
          <PanoramaSelectMenu
            placeholder={
              boundaries.data?.length === 0
                ? t(($) => $.planning.addBoundary.noneExisting)
                : undefined
            }
            disabled={boundaries.data?.length === 0}
            label={t(
              ($) =>
                $.planning[
                  mode === Mode.Boundary
                    ? 'selectBoundary'
                    : 'selectOrCreateBoundaryForPrescription'
                ]
            )}
            value={
              isNonNil(creationChoices.boundaryId)
                ? boundaries.data?.find(({ id }) => id === creationChoices.boundaryId)
                : undefined
            }
            onChange={({ id }) => {
              setCreationChoices({
                type: mode,
                boundaryId: id,
                auto:
                  id && mode === Mode.Boundary
                    ? AutoGenerateSelection.Manual
                    : AutoGenerateSelection.Unselected
              })
              if (mode !== Mode.Boundary) {
                setBoundaryId(id)
                if (id) {
                  loadBoundaryPreview.mutate({
                    params: { orgCode, boundaryId: id }
                  })
                } else {
                  map.drawTool.clear()
                }
              }
            }}
            options={boundaries?.data ?? []}
          />
        )}
        {(creationChoices.type === Mode.Guidance ? guidanceCreationOptions : creationOptions).map(
          ({ type, button, icon, auto }) => {
            const showsZoneSourcePicker = type === 'autoCreate' && picksOwnLayer
            const showsAutoCreateWarning =
              type === 'autoCreate' &&
              !picksOwnLayer &&
              autoCreateDisabled &&
              !isLoadingAutoCreateSource
            const description = showsZoneSourcePicker
              ? t(($) => $.planning.zoneTuning.description)
              : t(($) => $.planning.boundaryCreation[type].description[mode])

            return (
              <div
                className="flex w-full flex-col gap-2 rounded-lg border border-gray-300 p-4"
                key={type}
              >
                <div className="pan-title4">
                  {t(($) => $.planning.boundaryCreation[type].title)}
                </div>
                <p className="text-gray-500">{description}</p>
                {showsZoneSourcePicker && (
                  <ZoneSourcePicker
                    fieldId={fieldId}
                    source={zoneSource}
                    onSourceChange={onZoneSourceChange}
                  />
                )}
                {showsAutoCreateWarning && (
                  <div className="text-sm text-red-600">
                    {Mode.Guidance === mode
                      ? t(($) => $.planning.boundaryCreation.guidanceWarning)
                      : noAutoCreateSource
                        ? noAutoCreateSourceReason
                        : t(($) => $.planning.boundaryCreation.warning)}
                  </div>
                )}
                <PanoramaButton
                  kind={ButtonKind.Secondary}
                  onClick={() => {
                    if (type === 'import' && mode !== Mode.Guidance) {
                      setUpload(mode)
                    }
                    if (auto === AutoGenerateSelection.Auto) {
                      onAutoSelection()
                    }
                    setCreationChoices({ ...creationChoices, auto })
                  }}
                  disabledWhen={[
                    {
                      when: type === 'autoCreate' && isLoadingAutoCreateSource,
                      reason: t(($) => $.loading)
                    },
                    {
                      when: type === 'autoCreate' && noAutoCreateSource,
                      reason: noAutoCreateSourceReason
                    },
                    {
                      when: type === 'autoCreate' && autoCreateDisabled,
                      reason: t(($) => $.planning.boundaryCreation.warning)
                    },
                    {
                      when:
                        type !== 'import' &&
                        mode === Mode.Prescription &&
                        isNil(creationChoices.boundaryId),
                      reason: t(($) => $.planning.selectBoundary)
                    }
                  ]}
                  icon={icon}
                  className="w-full"
                >
                  {button}
                </PanoramaButton>
              </div>
            )
          }
        )}
        <BottomButtons
          onCancel={() => {
            map.drawTool.clear()
            onCancel()
          }}
        />
      </Container>
      <FilePlanAssetUploader
        saveBoundaryOnChange
        fieldId={fieldId}
        processSavedAssets={processSavedAssets}
        modalState={[upload, setUpload]}
      />
    </div>
  )
}
